import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    Select,
    Upload,
    message,
    Tabs,
    Tag,
    Space,
    Card,
    Row,
    Col
} from 'antd';
import {
    PlusOutlined,
    UploadOutlined,
    EditOutlined,
    DeleteOutlined,
    FilePdfOutlined,
    ExclamationCircleOutlined,
    SearchOutlined
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { confirm } = Modal;

const MAX_ARCHIVOS = 10;

// `simple` = solo registra fecha inicio, fecha fin y archivos adjuntos
const TIPOS_DOCUMENTO = [
    { key: 'personal', tab: 'Contratos Personal', singular: 'Contrato de Personal', simple: false },
    { key: 'tercero', tab: 'Contratos Terceros', singular: 'Contrato de Terceros', simple: false },
    { key: 'soat', tab: 'SOAT / Vehicular', singular: 'SOAT / Vehicular', simple: false },
    { key: 'revision_tecnica', tab: 'Revisión Técnica', singular: 'Revisión Técnica', simple: true },
    { key: 'sctr', tab: 'SCTR', singular: 'SCTR', simple: true },
    { key: 'poliza_seguro', tab: 'Pólizas de Seguro', singular: 'Póliza de Seguro', simple: true },
    { key: 'poliza_vida_ley', tab: 'Póliza Vida Ley', singular: 'Póliza de Vida Ley', simple: true },
];

const parseArchivos = (record) => {
    const archivos = record?.archivos_url;
    if (Array.isArray(archivos)) return archivos.filter(Boolean);
    if (typeof archivos === 'string' && archivos.trim()) {
        try {
            const parsed = JSON.parse(archivos);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [archivos];
        } catch {
            return [archivos];
        }
    }
    return record?.archivo_url ? [record.archivo_url] : [];
};

const GestionDocumental = () => {
    const { isMainAdmin, user } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalExtintorVisible, setModalExtintorVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();
    const [formExtintor] = Form.useForm();
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState(isMainAdmin ? 'all' : user?.empresa_id);
    const [fileList, setFileList] = useState([]);
    const [archivosExistentes, setArchivosExistentes] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [unidades, setUnidades] = useState([]);

    const tipoActual = TIPOS_DOCUMENTO.find((t) => t.key === activeTab);
    const esTipoSimple = !!tipoActual?.simple;

    useEffect(() => {
        fetchUnidades();
        if (isMainAdmin) {
            fetchEmpresas();
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [activeTab, empresaId]);

    const fetchEmpresas = async () => {
        try {
            const response = await api.get('/empresas');
            setEmpresas(response.data);
        } catch (error) {
            console.error('Error fetching empresas:', error);
        }
    };

    const fetchUnidades = async () => {
        try {
            const response = await api.get('/unidades');
            setUnidades(response.data);
        } catch (error) {
            console.error('Error fetching unidades:', error);
        }
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            let params = {};
            if (empresaId && empresaId !== 'all') {
                params.empresa_id = empresaId;
            }

            if (activeTab === 'extintores') {
                const response = await api.get('/extintores', { params });
                setData(response.data);
            } else {
                // Contratos (Personal, Tercero, SOAT)
                params.tipo = activeTab; // 'personal', 'tercero', 'soat'
                const response = await api.get('/contratos', { params });
                setData(response.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingItem(null);
        setFileList([]);
        setArchivosExistentes([]);
        if (activeTab === 'extintores') {
            formExtintor.resetFields();
            setModalExtintorVisible(true);
        } else {
            form.resetFields();
            // Pre-seleccionar tipo según tab
            form.setFieldsValue({ tipo_registro: activeTab });
            setModalVisible(true);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        setFileList([]);
        setArchivosExistentes(parseArchivos(record));
        if (activeTab === 'extintores') {
            formExtintor.setFieldsValue({
                ...record,
                fecha_ultima_recarga: record.fecha_ultima_recarga ? dayjs(record.fecha_ultima_recarga) : null,
                fecha_vencimiento: record.fecha_vencimiento ? dayjs(record.fecha_vencimiento) : null,
            });
            setModalExtintorVisible(true);
        } else {
            form.setFieldsValue({
                ...record,
                fecha_inicio: record.fecha_inicio ? dayjs(record.fecha_inicio) : null,
                fecha_fin: record.fecha_fin ? dayjs(record.fecha_fin) : null,
            });
            setModalVisible(true);
        }
    };

    const handleDelete = (id) => {
        confirm({
            title: '¿Estás seguro de eliminar este registro?',
            icon: <ExclamationCircleOutlined />,
            content: 'Esta acción no se puede deshacer.',
            onOk() {
                const endpoint = activeTab === 'extintores' ? `/extintores?id=${id}` : `/contratos?id=${id}`;
                api.delete(endpoint)
                    .then(() => {
                        message.success('Registro eliminado');
                        fetchData();
                    })
                    .catch(() => {
                        message.error('Error al eliminar registro');
                    });
            },
        });
    };

    const onFinishContrato = async (values) => {
        setUploading(true);
        const formData = new FormData();
        Object.keys(values).forEach(key => {
            if (values[key]) {
                if (key === 'fecha_inicio' || key === 'fecha_fin') {
                    formData.append(key, values[key].format('YYYY-MM-DD'));
                } else {
                    formData.append(key, values[key]);
                }
            }
        });

        fileList.forEach((file) => {
            formData.append('archivos[]', file);
        });

        if (editingItem) {
            // En los tipos de archivo único el nuevo adjunto reemplaza al anterior
            const conservados = !esTipoSimple && fileList.length > 0 ? [] : archivosExistentes;
            formData.append('archivos_existentes', JSON.stringify(conservados));
        }

        // Si es admin principal y seleccionó empresa
        if (isMainAdmin && values.empresa_id) {
            formData.append('empresa_id', values.empresa_id);
        }

        try {
            const endpoint = editingItem
                ? `/contratos?id=${editingItem.id}`
                : '/contratos';

            await api.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            message.success(editingItem ? 'Registro actualizado' : 'Registro creado');
            setModalVisible(false);
            fetchData();
        } catch (error) {
            console.error(error);
            message.error('Error al guardar el registro');
        } finally {
            setUploading(false);
        }
    };

    const onFinishExtintor = async (values) => {
        setUploading(true);
        const formData = new FormData();
        Object.keys(values).forEach(key => {
            if (values[key]) {
                if (key === 'fecha_ultima_recarga' || key === 'fecha_vencimiento') {
                    formData.append(key, values[key].format('YYYY-MM-DD'));
                } else {
                    formData.append(key, values[key]);
                }
            }
        });

        if (fileList.length > 0) {
            formData.append('archivo', fileList[0]);
        }

        if (isMainAdmin && values.empresa_id) {
            formData.append('empresa_id', values.empresa_id);
        }

        try {
            const endpoint = editingItem
                ? `/extintores?id=${editingItem.id}`
                : '/extintores';

            await api.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            message.success(editingItem ? 'Extintor actualizado' : 'Extintor registrado');
            setModalExtintorVisible(false);
            fetchData();
        } catch (error) {
            console.error(error);
            message.error('Error al guardar extintor');
        } finally {
            setUploading(false);
        }
    };

    const buildUploadProps = (multiple = false) => ({
        multiple,
        onRemove: (file) => {
            setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
        },
        beforeUpload: (file) => {
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('El archivo debe ser menor a 5MB!');
                return Upload.LIST_IGNORE;
            }
            if (!multiple) {
                setFileList([file]);
                return false;
            }
            if (archivosExistentes.length + fileList.length >= MAX_ARCHIVOS) {
                message.warning(`Puede adjuntar hasta ${MAX_ARCHIVOS} archivos`);
                return Upload.LIST_IGNORE;
            }
            setFileList((prev) => [...prev, file]);
            return false; // Manual upload
        },
        fileList,
    });

    const uploadProps = buildUploadProps(false);

    const handleQuitarArchivoExistente = (url) => {
        setArchivosExistentes((prev) => prev.filter((item) => item !== url));
    };

    const renderStatus = (estado, color) => (
        <Tag color={color}>{estado.toUpperCase()}</Tag>
    );

    const renderArchivos = (record) => {
        const archivos = parseArchivos(record);
        if (archivos.length === 0) return '-';

        return (
            <Space direction="vertical" size={0}>
                {archivos.map((url, index) => (
                    <a
                        key={url}
                        href={`${process.env.REACT_APP_API_URL}${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FilePdfOutlined /> {archivos.length > 1 ? `Ver ${index + 1}` : 'Ver'}
                    </a>
                ))}
            </Space>
        );
    };

    const columnsContratos = [
        {
            title: esTipoSimple ? 'Descripción / Referencia' : 'Entidad / Titular',
            dataIndex: 'nombre_entidad',
            key: 'nombre_entidad',
            render: (text) => text || '-',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={esTipoSimple ? 'Buscar referencia' : 'Buscar titular'}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Buscar
                        </Button>
                        <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.nombre_entidad
                    ? record.nombre_entidad.toString().toLowerCase().includes(value.toLowerCase())
                    : '',
        },
        ...(esTipoSimple ? [] : [{
            title: 'Documento',
            dataIndex: 'documento_entidad',
            key: 'documento_entidad',
            render: (text) => text || '-',
        }]),
        {
            title: 'Fecha Inicio',
            dataIndex: 'fecha_inicio',
            key: 'fecha_inicio',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Vencimiento',
            dataIndex: 'fecha_fin',
            key: 'fecha_fin',
            render: (text) => dayjs(text).format('DD/MM/YYYY'),
            sorter: (a, b) => new Date(a.fecha_fin) - new Date(b.fecha_fin),
        },
        {
            title: 'Estado',
            key: 'estado',
            render: (_, record) => renderStatus(record.estado, record.color),
            // filters: [{ text: 'Vigente', value: 'Vigente' }, { text: 'Por vencer', value: 'Por vencer' }, { text: 'Vencido', value: 'Vencido' }],
            // onFilter: (value, record) => record.estado === value,
        },
        {
            title: esTipoSimple ? 'Archivos' : 'Archivo',
            key: 'archivo',
            render: (_, record) => renderArchivos(record)
        },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    {isMainAdmin && <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />}
                </Space>
            ),
        },
    ];

    const columnsExtintores = [
        {
            title: 'Unidad / Código',
            key: 'unidad',
            render: (_, record) => record.unidad_placa || record.codigo_unidad || '-'
        },
        {
            title: 'Ubicación',
            dataIndex: 'ubicacion',
            key: 'ubicacion'
        },
        {
            title: 'Tipo Extintor',
            dataIndex: 'tipo_extintor',
            key: 'tipo_extintor'
        },
        {
            title: 'Última Recarga',
            dataIndex: 'fecha_ultima_recarga',
            key: 'fecha_ultima_recarga',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Próxima Recarga',
            dataIndex: 'fecha_vencimiento',
            key: 'fecha_vencimiento',
            render: (text) => dayjs(text).format('DD/MM/YYYY'),
            sorter: (a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento),
        },
        {
            title: 'Estado',
            key: 'estado',
            render: (_, record) => renderStatus(record.estado, record.color)
        },
        {
            title: 'Certificado',
            key: 'archivo',
            render: (_, record) => record.archivo_url ? (
                <a href={`${process.env.REACT_APP_API_URL}${record.archivo_url}`} target="_blank" rel="noopener noreferrer">
                    <FilePdfOutlined /> Ver
                </a>
            ) : '-'
        },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    {isMainAdmin && <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Gestión de Documentos y Vencimientos</h2>
                <Space>
                    {isMainAdmin && (
                        <Select
                            value={empresaId}
                            onChange={setEmpresaId}
                            style={{ width: 200 }}
                            placeholder="Filtrar por empresa"
                        >
                            <Select.Option value="all">Todas las empresas</Select.Option>
                            {empresas.map((e) => (
                                <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>
                            ))}
                        </Select>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        Nuevo Registro
                    </Button>
                </Space>
            </div>

            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    {TIPOS_DOCUMENTO.map((tipo) => (
                        <TabPane tab={tipo.tab} key={tipo.key} />
                    ))}
                    <TabPane tab="Extintores" key="extintores" />
                </Tabs>

                <Table
                    columns={activeTab === 'extintores' ? columnsExtintores : columnsContratos}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Modal Contratos */}
            <Modal
                title={`${editingItem ? 'Editar' : 'Nuevo'} ${tipoActual?.singular || 'Registro'}`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                <Form form={form} onFinish={onFinishContrato} layout="vertical">
                    <Form.Item name="tipo_registro" hidden><Input /></Form.Item>

                    {esTipoSimple ? (
                        <Form.Item
                            name="nombre_entidad"
                            label="Descripción / Referencia (opcional)"
                            tooltip="Sirve para identificar el registro en el listado, por ejemplo la placa o el nombre del trabajador"
                        >
                            <Input placeholder="Ej. Placa ABC-123 o Juan Pérez" />
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item
                                name="nombre_entidad"
                                label="Titular / Entidad"
                                rules={[{ required: true, message: 'Ingrese el nombre' }]}
                            >
                                <Input placeholder="Nombre del trabajador, empresa o vehículo" />
                            </Form.Item>

                            <Form.Item name="documento_entidad" label="Documento Identidad / RUC / Placa">
                                <Input placeholder="DNI, RUC o Placa" />
                            </Form.Item>
                        </>
                    )}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="fecha_inicio"
                                label="Fecha Inicio"
                                rules={esTipoSimple ? [{ required: true, message: 'Requerido' }] : []}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="fecha_fin"
                                label="Fecha Vencimiento"
                                rules={[{ required: true, message: 'Requerido' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {!esTipoSimple && (
                        <Form.Item name="observaciones" label="Observaciones">
                            <TextArea rows={3} />
                        </Form.Item>
                    )}

                    {isMainAdmin && (
                        <Form.Item name="empresa_id" label="Empresa" rules={[{ required: true }]}>
                            <Select placeholder="Seleccionar empresa">
                                {empresas.map(e => (
                                    <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        label={esTipoSimple ? 'Adjuntar Archivos' : 'Adjuntar Archivo'}
                        extra={esTipoSimple
                            ? `Hasta ${MAX_ARCHIVOS} archivos, máx 5MB cada uno (PDF/Imagen)`
                            : 'Máx 5MB (PDF/Imagen)'}
                    >
                        {archivosExistentes.length > 0 && (
                            <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 8 }}>
                                {archivosExistentes.map((url, index) => (
                                    <Space key={url}>
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}${url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FilePdfOutlined /> Archivo {index + 1}
                                        </a>
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleQuitarArchivoExistente(url)}
                                        />
                                    </Space>
                                ))}
                            </Space>
                        )}
                        <Upload {...buildUploadProps(esTipoSimple)}>
                            <Button icon={<UploadOutlined />}>Seleccionar Archivo</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={uploading} block>
                            Guardar
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Extintores */}
            <Modal
                title={`${editingItem ? 'Editar' : 'Nuevo'} Registro Extintor`}
                open={modalExtintorVisible}
                onCancel={() => setModalExtintorVisible(false)}
                footer={null}
            >
                <Form form={formExtintor} onFinish={onFinishExtintor} layout="vertical">

                    <Form.Item label="Unidad (Opcional)" name="unidad_id">
                        <Select allowClear placeholder="Seleccionar unidad vinculada">
                            {unidades.map(u => (
                                <Select.Option key={u.id} value={u.id}>{u.placa} - {u.marca}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="codigo_unidad"
                        label="Código / ID Interno"
                        tooltip="Si no selecciona una unidad de la lista"
                    >
                        <Input placeholder="Código del extintor o placa manual" />
                    </Form.Item>

                    <Form.Item name="ubicacion" label="Ubicación Física">
                        <Input placeholder="Ej. Cabina, Taller, Oficina" />
                    </Form.Item>

                    <Form.Item name="tipo_extintor" label="Tipo de Extintor">
                        <Select>
                            <Select.Option value="PQS">PQS (Polvo Químico Seco)</Select.Option>
                            <Select.Option value="CO2">CO2</Select.Option>
                            <Select.Option value="Agua">Agua</Select.Option>
                            <Select.Option value="Espuma">Espuma</Select.Option>
                            <Select.Option value="Otro">Otro</Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="fecha_ultima_recarga" label="Última Recarga">
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="fecha_vencimiento"
                                label="Próxima Recarga (Vencimiento)"
                                rules={[{ required: true, message: 'Requerido' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="observaciones" label="Observaciones">
                        <TextArea rows={3} />
                    </Form.Item>

                    {isMainAdmin && (
                        <Form.Item name="empresa_id" label="Empresa" rules={[{ required: true }]}>
                            <Select placeholder="Seleccionar empresa">
                                {empresas.map(e => (
                                    <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label="Adjuntar Certificado" extra="Máx 5MB (PDF/Imagen)">
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />}>Seleccionar Archivo</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={uploading} block>
                            Guardar
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default GestionDocumental;
